/**
 * Marketplace Alternatives Cluster — Feb 2026 (P1.5 SEO Sprint)
 *
 * Powers 8 "Best Alternatives to X" pages targeting high-intent decision
 * queries. Each page renders from /pages/MarketplaceAlternativesPage.jsx
 * using this config + MARKETPLACE_ATTRS from marketplaceComparisonData.js.
 *
 * Architecture:
 *   ALT_ATTRS          – extra attribute records for marketplaces not in the
 *                        comparison-data attrs table (Meesho, Purplle, Tira, etc.)
 *   ATTRS              – merged attribute lookup (comparison attrs ⊕ ALT_ATTRS)
 *   ALTERNATIVES       – array of 8 alternatives pages (primary + 4-5 alts each)
 *   ALT_BY_SLUG        – lookup
 *   ALT_PATHS          – route auto-derivation
 *   getAlternativesByMarketplace – internal-linking helper for hub pages
 */
import { MARKETPLACE_ATTRS } from './marketplaceComparisonData';

// ── Additional attribute records for alternative-only marketplaces ────
export const ALT_ATTRS = {
  meesho: {
    name: 'Meesho',
    slug: 'meesho',
    logo: 'MS',
    accent: 'from-pink-500 to-purple-500',
    timeline: '5-10 days',
    approvalDifficulty: 'Low',
    commission: '0% (fixed fee)',
    gmv: '₹32,000 Cr',
    monthlyActiveUsers: '150M+',
    aovBand: '₹250-600',
    categoryFocus: 'Mass-market Fashion, Home, Beauty, Grocery',
    audienceProfile: 'Tier-2/3/4 India, value-conscious shoppers, 60%+ women buyers',
    growthOpportunities: 'India\'s largest tier-2/3 marketplace; zero commission model; 150M+ MAU; 24% YoY',
    hubHref: '/marketplaces',
    approvalHref: '/marketplaces',
    pros: [
      'Zero commission (only fixed shipping fee)',
      'Fastest onboarding (5-10 days)',
      '150M+ MAU with deep tier-2/3/4 reach',
      'No brand-registry gating — accepts unbranded / private-label',
    ],
    cons: [
      'AOV ₹250-600 — hard for premium brands',
      'Minimal editorial curation',
      'Return/RTO rates 30-40%',
    ],
  },
  flipkart: {
    name: 'Flipkart',
    slug: 'flipkart',
    logo: 'FK',
    accent: 'from-blue-500 to-yellow-500',
    timeline: '10-18 days',
    approvalDifficulty: 'Low',
    commission: '5-25%',
    gmv: '₹180,000 Cr',
    monthlyActiveUsers: '200M+',
    aovBand: '₹550-1,400',
    categoryFocus: 'Everything (Fashion, Electronics, Grocery, Beauty)',
    audienceProfile: 'Pan-India mass + tier-2/3, Walmart-backed, 200M+ MAU',
    growthOpportunities: 'Second-largest India marketplace; Flipkart Fashion + Myntra combo covers 90% fashion shoppers',
    hubHref: '/marketplaces',
    approvalHref: '/marketplaces',
    pros: [
      '₹180,000 Cr GMV — 2nd largest in India',
      'Flipkart Assured + F-Assured badges drive conversion',
      'Big Billion Days + Flipkart Plus loyalty',
      'Lower commission than Myntra (5-25%)',
    ],
    cons: [
      'Fashion curation less polished vs Myntra',
      'PLA-heavy — high ad spend required',
      'Return rate 20-25% on fashion',
    ],
  },
  purplle: {
    name: 'Purplle',
    slug: 'purplle',
    logo: 'PP',
    accent: 'from-purple-600 to-fuchsia-600',
    timeline: '18-25 days',
    approvalDifficulty: 'Medium',
    commission: '20-32%',
    gmv: '₹1,800 Cr',
    monthlyActiveUsers: '10M+',
    aovBand: '₹450-1,200',
    categoryFocus: 'Beauty, Cosmetics, Personal Care',
    audienceProfile: 'Tier-2/3 beauty consumer, gen-Z + young millennials, price-conscious',
    growthOpportunities: 'Fastest-growing challenger to Nykaa in tier-2/3; strong own-brand ecosystem (Purplle Faces Canada, Good Vibes, etc.)',
    hubHref: '/marketplaces',
    approvalHref: '/marketplaces',
    pros: [
      'Tier-2/3 beauty penetration (Nykaa\'s soft flank)',
      'Lower AOV floor — accepts mass beauty brands',
      'Aggressive challenger-brand positioning',
    ],
    cons: [
      'Lower GMV (₹1,800 Cr) vs Nykaa',
      'Less editorial content velocity',
      'Own-brands compete with 3P brands',
    ],
  },
  tira: {
    name: 'Tira',
    slug: 'tira',
    logo: 'TR',
    accent: 'from-rose-500 to-red-500',
    timeline: '30-35 days',
    approvalDifficulty: 'High',
    commission: '20-30%',
    gmv: '₹800 Cr',
    monthlyActiveUsers: '5M+',
    aovBand: '₹2,400-4,800',
    categoryFocus: 'Premium Beauty, Cosmetics, Personal Care',
    audienceProfile: 'UHNW + HNW premium beauty consumer, Tira.com + app + 100+ flagship stores',
    growthOpportunities: 'Reliance-backed; omnichannel (100+ stores); highest AOV in beauty; year-2 target for Nykaa graduates',
    hubHref: '/tira-brand-approval',
    approvalHref: '/tira-brand-approval',
    pros: [
      'Omnichannel: online + 100+ flagship stores',
      'Highest AOV band in beauty (₹2,400-4,800)',
      'Reliance ecosystem cross-promo',
    ],
    cons: [
      'Slowest approval (30-35 days)',
      'Lower GMV (₹800 Cr) — still ramping',
      'Requires omnichannel readiness',
    ],
  },
  ajioLuxe: {
    name: 'AJIO Luxe',
    slug: 'ajio-luxe',
    logo: 'AL',
    accent: 'from-amber-600 to-yellow-500',
    timeline: '20-30 days',
    approvalDifficulty: 'High',
    commission: '22-32%',
    gmv: '₹1,200 Cr',
    monthlyActiveUsers: '8M+',
    aovBand: '₹3,800-8,400',
    categoryFocus: 'Luxury + designer fashion, footwear, accessories',
    audienceProfile: 'HNW luxury consumer, brand-conscious, premium AOV',
    growthOpportunities: 'AJIO Luxe track — Reliance-owned luxury lane; alternative to Tata CLiQ Luxury; growing 45% YoY',
    hubHref: '/ajio-account-management',
    approvalHref: '/ajio-seller-registration',
    pros: [
      'Reliance-backed luxury lane',
      '₹3,800-8,400 AOV band',
      'Reliance ecosystem cross-promo (JioMart Luxe, Trends)',
    ],
    cons: [
      'Lower GMV (₹1,200 Cr) vs Tata CLiQ Luxury',
      'Curation tighter than main AJIO tracks',
    ],
  },
  bbnow: {
    name: 'BB Now',
    slug: 'bb-now',
    logo: 'BB',
    accent: 'from-green-500 to-emerald-600',
    timeline: '20-28 days',
    approvalDifficulty: 'Medium',
    commission: '22-35%',
    gmv: '₹3,200 Cr',
    monthlyActiveUsers: '18M+',
    aovBand: '₹380-720',
    categoryFocus: 'Quick Commerce, Grocery, FMCG, Fresh, Personal Care',
    audienceProfile: 'Tata-owned QC + grocery consumer, tier-1/2 metros, Tata Neu ecosystem',
    growthOpportunities: 'Tata-backed Quick Commerce; BB Now = QC + BigBasket = full basket; Tata Neu ecosystem cross-promo',
    hubHref: '/marketplaces',
    approvalHref: '/bigbasket-seller-registration',
    pros: [
      'Tata Group backing + Neu loyalty',
      'BB Now (QC) + BigBasket (basket) dual play',
      '18M+ MAU with tier-1/2 metro dominance',
    ],
    cons: [
      'Lower QC GMV (₹3,200 Cr) vs Blinkit/Zepto',
      'Approval slower than Blinkit/Zepto',
    ],
  },
  jiomart: {
    name: 'JioMart',
    slug: 'jiomart',
    logo: 'JM',
    accent: 'from-blue-600 to-cyan-500',
    timeline: '18-28 days',
    approvalDifficulty: 'Medium',
    commission: '20-30%',
    gmv: '₹22,000 Cr',
    monthlyActiveUsers: '25M+',
    aovBand: '₹450-1,200',
    categoryFocus: 'Grocery, Fashion, Home, Electronics, Beauty (all-category)',
    audienceProfile: 'Reliance ecosystem consumer, tier-2/3 mass, Jio-integrated (500M+ Jio subs cross-promo)',
    growthOpportunities: 'Reliance-backed; JioMart Express (QC arm); pan-India Reliance Retail integration',
    hubHref: '/marketplaces',
    approvalHref: '/marketplaces',
    pros: [
      'Reliance ecosystem (500M+ Jio subs cross-promo)',
      'Multi-category (grocery + fashion + electronics)',
      'JioMart Express = QC arm launched 2024',
    ],
    cons: [
      'GMV lower than category leaders',
      'Category depth uneven across verticals',
    ],
  },
};

// Merged attribute lookup — always resolve via ATTRS
const ATTRS = { ...MARKETPLACE_ATTRS, ...ALT_ATTRS };

const build = (spec) => {
  const primary = ATTRS[spec.primaryKey];
  return {
    slug: `best-alternatives-to-${primary.slug}${spec.qualifier ? `-${spec.qualifier}` : ''}`,
    primary,
    alternatives: spec.altKeys.map((k) => ATTRS[k]),
    scopeLabel: spec.scopeLabel || primary.name,
    metaTitle: spec.metaTitle,
    metaDescription: spec.metaDescription,
    directAnswer: spec.directAnswer,
    whyLookForAlternatives: spec.whyLookForAlternatives,
    faqs: spec.faqs || [],
    relatedComparisons: spec.relatedComparisons || [],
  };
};

// ── 8 Alternatives Pages ──────────────────────────────────────────────
export const ALTERNATIVES = [
  build({
    primaryKey: 'myntra',
    altKeys: ['ajio', 'tataCliq', 'amazon', 'flipkart', 'meesho'],
    scopeLabel: 'Myntra',
    metaTitle: 'Best Alternatives to Myntra 2026 — Fashion Marketplaces Compared | Zaroori Retail',
    metaDescription: 'Top 5 alternatives to Myntra for fashion brands in 2026: AJIO, Tata CLiQ, Amazon, Flipkart, Meesho. Commission, approval, brand fit + who should choose each.',
    directAnswer:
      'The best alternatives to Myntra for fashion brands in 2026 are AJIO (₹14,500 Cr GMV, 3-track flexibility), Tata CLiQ (luxury cross-category), Amazon (volume + tier-2/3 reach), Flipkart Fashion (₹180,000 Cr platform), and Meesho (mass tier-2/3/4). Most fashion brands run Myntra + AJIO in parallel, then add 1-2 of these for tier-2/3 volume and luxury positioning.',
    whyLookForAlternatives:
      'Brands look for Myntra alternatives when: (1) approval is delayed or rejected, (2) commission of 18-32% is too high for margin structure, (3) editorial curation excludes their category (e.g. mass-market or unbranded), (4) tier-2/3/4 reach is a strategic priority, or (5) they need luxury positioning that Myntra\'s mass-fashion audience does not deliver.',
    faqs: [
      { q: 'Which alternative to Myntra has the lowest commission?', a: 'Meesho has effectively zero commission (only fixed shipping fee), followed by Amazon at 8-25% and Flipkart at 5-25%. AJIO and Tata CLiQ are in the 15-32% range, similar to Myntra.' },
      { q: 'Which Myntra alternative has the fastest approval?', a: 'Meesho (5-10 days) is fastest, followed by Amazon (7-14 days), Flipkart (10-18 days), and AJIO (16-26 days). Tata CLiQ takes the longest at 25-40 days.' },
      { q: 'Should I move OFF Myntra or add these in parallel?', a: 'Almost never move off Myntra — its 70M+ MAU is unmatched for fashion. Instead, add AJIO first for scale, then Amazon for tier-2/3 volume + FBA logistics, then Tata CLiQ for luxury positioning if AOV supports it.' },
      { q: 'Which Myntra alternative is best for emerging D2C brands?', a: 'AJIO Indie track (₹1,200+ AOV, 16-26 days approval) is the best emerging-brand alternative to Myntra. Meesho works if you can operate at ₹250-600 AOV.' },
    ],
    relatedComparisons: ['myntra-vs-ajio', 'myntra-vs-tata-cliq', 'amazon-vs-myntra', 'nykaa-vs-myntra'],
  }),
  build({
    primaryKey: 'ajio',
    altKeys: ['myntra', 'tataCliq', 'flipkart', 'meesho', 'amazon'],
    scopeLabel: 'AJIO',
    metaTitle: 'Best Alternatives to AJIO 2026 — Fashion Marketplaces Compared | Zaroori Retail',
    metaDescription: 'Top 5 alternatives to AJIO for fashion brands in 2026: Myntra, Tata CLiQ, Flipkart, Meesho, Amazon. Commission, approval, 3-track vs curated model compared.',
    directAnswer:
      'The best alternatives to AJIO for fashion brands are Myntra (₹35,000 Cr GMV, editorial curation), Tata CLiQ (luxury cross-category), Flipkart Fashion (mass + tier-2/3), Meesho (tier-2/3/4 mass), and Amazon (volume + FBA). AJIO\'s 3-track structure is unique — brands typically retain AJIO for Indie/Luxe flexibility and add Myntra as the primary volume driver.',
    whyLookForAlternatives:
      'Brands consider AJIO alternatives when: (1) settlement stretches to T+30 which strains cash flow, (2) AJIO Ads self-serve beta lacks the ad-tool depth of Myntra PLA / Amazon SP, (3) the 3-track segmentation restricts AOV band, or (4) they need higher editorial content velocity than AJIO delivers.',
    faqs: [
      { q: 'Which alternative to AJIO has the largest fashion audience?', a: 'Myntra with 70M+ MAU is the largest fashion audience alternative — 2x AJIO. For all-category fashion, Flipkart Fashion has 200M+ MAU but less fashion-native intent.' },
      { q: 'Should I choose Myntra or Tata CLiQ over AJIO for luxury?', a: 'For pure luxury, Tata CLiQ Luxury outperforms AJIO Luxe on brand equity. For scale luxury + emerging designers, AJIO Luxe remains competitive. Most luxury brands launch on both.' },
      { q: 'What is the fastest-to-launch AJIO alternative?', a: 'Meesho (5-10 days) and Amazon (7-14 days) beat AJIO\'s 16-26 days by 2-3 weeks.' },
    ],
    relatedComparisons: ['myntra-vs-ajio', 'tata-cliq-vs-ajio'],
  }),
  build({
    primaryKey: 'nykaa',
    qualifier: 'fashion',
    altKeys: ['myntra', 'ajio', 'tataCliq', 'ajioLuxe', 'amazon'],
    scopeLabel: 'Nykaa Fashion',
    metaTitle: 'Best Alternatives to Nykaa Fashion 2026 — Premium Fashion Marketplaces | Zaroori Retail',
    metaDescription: 'Top 5 alternatives to Nykaa Fashion for premium fashion brands in 2026: Myntra, AJIO, Tata CLiQ, AJIO Luxe, Amazon. Approval, commission, curation compared.',
    directAnswer:
      'The best alternatives to Nykaa Fashion for premium fashion brands are Myntra (₹35,000 Cr fashion GMV, 70M+ MAU), AJIO (3-track structure), Tata CLiQ Luxury (heritage + designer), AJIO Luxe (Reliance luxury), and Amazon (volume + FBA logistics). Most premium fashion brands lead with Myntra + AJIO Luxe, then add Tata CLiQ Luxury for heritage positioning.',
    whyLookForAlternatives:
      'Nykaa Fashion is Nykaa\'s fashion vertical but does not have the shopper intent or scale of Myntra/AJIO. Brands look for alternatives to reach a larger fashion-native audience, secure better editorial placement, or access luxury lanes not present on Nykaa Fashion.',
    faqs: [
      { q: 'Is Nykaa Fashion better than Myntra for premium fashion?', a: 'No — Myntra dominates premium fashion with 70M+ fashion-native MAU. Nykaa Fashion is a smaller, beauty-adjacent lane. Use Nykaa Fashion as an add-on, not a primary channel.' },
      { q: 'Which alternative to Nykaa Fashion has the best luxury track?', a: 'Tata CLiQ Luxury (Tata Group equity, ₹3,500+ AOV floor) and AJIO Luxe (Reliance-backed, ₹3,800+ AOV) are the strongest luxury alternatives.' },
    ],
    relatedComparisons: ['nykaa-vs-myntra', 'myntra-vs-ajio', 'tata-cliq-vs-ajio'],
  }),
  build({
    primaryKey: 'tataCliq',
    altKeys: ['myntra', 'ajio', 'ajioLuxe', 'amazon', 'nykaa'],
    scopeLabel: 'Tata CLiQ',
    metaTitle: 'Best Alternatives to Tata CLiQ 2026 — Luxury & Cross-Category Marketplaces | Zaroori Retail',
    metaDescription: 'Top 5 alternatives to Tata CLiQ for luxury + cross-category brands: Myntra, AJIO, AJIO Luxe, Amazon, Nykaa. Commission, approval, curation compared.',
    directAnswer:
      'The best alternatives to Tata CLiQ for luxury + cross-category brands are AJIO Luxe (Reliance luxury lane), Myntra (mass + bridge-premium fashion at scale), AJIO (3-track structure), Nykaa (dedicated beauty), and Amazon (volume + FBA). Luxury brands typically retain Tata CLiQ Luxury as a badge channel and drive volume from AJIO Luxe + Myntra.',
    whyLookForAlternatives:
      'Brands seek Tata CLiQ alternatives when: (1) 25-40 day approval is slower than competitors, (2) GMV (₹4,200 Cr) is smaller than Myntra/Amazon, (3) they need higher volume than Tata CLiQ\'s curated audience delivers, or (4) their AOV falls below the ₹3,500 Luxury floor.',
    faqs: [
      { q: 'Is AJIO Luxe a real alternative to Tata CLiQ Luxury?', a: 'Yes — AJIO Luxe (Reliance-backed) is Tata CLiQ Luxury\'s direct luxury-lane competitor. Both have ₹3,500-8,400 AOV bands. Most luxury brands launch on both in parallel.' },
      { q: 'Which alternative has the largest cross-category reach?', a: 'Amazon (₹280,000 Cr GMV) followed by Flipkart (₹180,000 Cr) — both offer cross-category scale that Tata CLiQ Palette cannot match.' },
    ],
    relatedComparisons: ['tata-cliq-vs-ajio', 'nykaa-vs-tata-cliq', 'myntra-vs-tata-cliq'],
  }),
  build({
    primaryKey: 'amazon',
    qualifier: 'fashion',
    altKeys: ['myntra', 'ajio', 'flipkart', 'tataCliq', 'meesho'],
    scopeLabel: 'Amazon Fashion',
    metaTitle: 'Best Alternatives to Amazon Fashion 2026 — Curated Fashion Marketplaces | Zaroori Retail',
    metaDescription: 'Top 5 alternatives to Amazon Fashion for fashion brands: Myntra, AJIO, Flipkart, Tata CLiQ, Meesho. Commission, approval, curation vs volume compared.',
    directAnswer:
      'The best alternatives to Amazon Fashion for fashion brands are Myntra (curated ₹35,000 Cr fashion GMV), AJIO (3-track structure), Flipkart Fashion (mass volume), Tata CLiQ (luxury cross-category), and Meesho (tier-2/3/4). Most fashion brands lead with Myntra + AJIO for curation and add Amazon Fashion for volume + FBA + tier-2/3 reach.',
    whyLookForAlternatives:
      'Amazon Fashion is a discovery-secondary channel — most brands add it for volume rather than lead with it. Alternatives are chosen when: (1) editorial curation is needed to build brand equity, (2) fashion-native shopper intent matters more than volume, or (3) higher AOV bands (₹1,650+) are required.',
    faqs: [
      { q: 'Is Myntra a better alternative to Amazon Fashion?', a: 'Yes for premium D2C fashion — Myntra\'s fashion-native 70M+ MAU converts at higher AOV (₹1,650-2,400) than Amazon Fashion. For volume + tier-2/3, retain Amazon Fashion.' },
      { q: 'Which Amazon Fashion alternative has the best ad tools?', a: 'Myntra (PLA + Sponsored Brand + Display + Video) matches Amazon\'s ad-tool depth for fashion. AJIO Ads is in self-serve beta.' },
    ],
    relatedComparisons: ['amazon-vs-myntra', 'myntra-vs-ajio'],
  }),
  build({
    primaryKey: 'blinkit',
    altKeys: ['zepto', 'instamart', 'bbnow', 'jiomart', 'amazon'],
    scopeLabel: 'Blinkit',
    metaTitle: '5 Best Blinkit Alternatives (2026): Zepto, Instamart, BB Now Compared',
    metaDescription: 'Top 5 alternatives to Blinkit for Quick Commerce brands: Zepto, Swiggy Instamart, BB Now, JioMart, Amazon. Commission, dark-store reach, delivery time compared.',
    directAnswer:
      'The best alternatives to Blinkit for Quick Commerce brands are Zepto (10-min delivery, ₹14,200 Cr), Swiggy Instamart (Swiggy 500M+ MAU cross-app), BB Now (Tata-backed, BigBasket dual play), JioMart Express (Reliance ecosystem), and Amazon (2-hour delivery + FBA). Most impulse-buy brands run Blinkit + Zepto + Instamart in parallel.',
    whyLookForAlternatives:
      'Brands look for Blinkit alternatives when: (1) commission of 25-40% is too high, (2) dark-store SKU cap forces them out, (3) tier-1 metro-only reach is limiting, or (4) they need cross-app discovery from a larger ecosystem.',
    faqs: [
      { q: 'Which Blinkit alternative has the fastest delivery?', a: 'Zepto (10-min delivery) beats Blinkit (15-min). Instamart is 15-min. BB Now and JioMart Express are 15-30 min.' },
      { q: 'Which has the largest audience?', a: 'Swiggy Instamart (via Swiggy app 500M+ food-delivery MAU cross-app) has the largest addressable audience, though Instamart-specific GMV is ₹9,800 Cr.' },
    ],
    relatedComparisons: ['blinkit-vs-zepto', 'zepto-vs-swiggy-instamart'],
  }),
  build({
    primaryKey: 'zepto',
    altKeys: ['blinkit', 'instamart', 'bbnow', 'jiomart', 'amazon'],
    scopeLabel: 'Zepto',
    metaTitle: 'Best Alternatives to Zepto 2026 — Quick Commerce Platforms Compared | Zaroori Retail',
    metaDescription: 'Top 5 alternatives to Zepto for Quick Commerce brands: Blinkit, Swiggy Instamart, BB Now, JioMart, Amazon. GMV, commission, delivery time compared.',
    directAnswer:
      'The best alternatives to Zepto for Quick Commerce brands are Blinkit (₹18,500 Cr GMV, Zomato-owned), Swiggy Instamart (Swiggy 500M+ MAU cross-app), BB Now (Tata + BigBasket dual play), JioMart Express (Reliance), and Amazon (2-hour + FBA logistics). Most brands launch on Blinkit + Zepto + Instamart in parallel for maximum dark-store SKU velocity.',
    whyLookForAlternatives:
      'Brands seek Zepto alternatives when: (1) tier-1 metro-only reach limits their tier-2/3 growth, (2) commission of 25-38% is too high, (3) they need Zomato/Swiggy cross-app discovery, or (4) they want the Tata Neu / Reliance loyalty ecosystem lift.',
    faqs: [
      { q: 'Which alternative to Zepto has the largest QC GMV?', a: 'Blinkit (₹18,500 Cr, Zomato-owned) is the largest QC platform by GMV.' },
      { q: 'Which has better tier-2/3 reach?', a: 'BB Now (Tata-backed, BigBasket network) and JioMart Express (Reliance retail integration) have better tier-2/3 reach than Zepto.' },
    ],
    relatedComparisons: ['blinkit-vs-zepto', 'zepto-vs-swiggy-instamart'],
  }),
  build({
    primaryKey: 'instamart',
    altKeys: ['blinkit', 'zepto', 'bbnow', 'jiomart', 'amazon'],
    scopeLabel: 'Swiggy Instamart',
    metaTitle: 'Best Alternatives to Swiggy Instamart 2026 — Quick Commerce Compared | Zaroori Retail',
    metaDescription: 'Top 5 alternatives to Swiggy Instamart for QC brands: Blinkit, Zepto, BB Now, JioMart, Amazon. Commission, GMV, cross-app discovery compared.',
    directAnswer:
      'The best alternatives to Swiggy Instamart for Quick Commerce brands are Blinkit (₹18,500 Cr, 15-min delivery), Zepto (10-min, tier-1 premium), BB Now (Tata + BigBasket dual play), JioMart Express (Reliance), and Amazon (2-hour + FBA). Most F&B brands lead with Instamart for Swiggy cross-app, then add Blinkit + Zepto for pure velocity.',
    whyLookForAlternatives:
      'Brands look for Instamart alternatives when: (1) lower GMV (₹9,800 Cr) limits volume vs Blinkit, (2) Swiggy margin structure squeezes unit economics, (3) they need dedicated-app recall (vs Swiggy sub-app), or (4) commission of 26-38% is too high.',
    faqs: [
      { q: 'Which alternative has the best F&B category focus?', a: 'Swiggy Instamart itself is the F&B-strongest QC platform (Swiggy food-delivery cross-sell). Blinkit + Zepto are broader impulse-buy; BB Now is grocery-strongest.' },
      { q: 'Should I launch on all four (Blinkit + Zepto + Instamart + BB Now)?', a: 'For impulse-buy FMCG and D2C beauty travel sizes, yes — parallel launches capture unique consumer cohorts on each dark-store network.' },
    ],
    relatedComparisons: ['zepto-vs-swiggy-instamart', 'blinkit-vs-zepto'],
  }),
];

export const ALT_BY_SLUG = Object.fromEntries(ALTERNATIVES.map((a) => [a.slug, a]));

export const ALT_PATHS = ALTERNATIVES.map((a) => `/${a.slug}`);

// Internal linking helper — used by hub pages
export const getAlternativesByMarketplace = (marketplaceKey) => {
  const target =
    marketplaceKey === 'instamart' ? 'swiggy-instamart' :
    marketplaceKey === 'tataCliq' ? 'tata-cliq' :
    marketplaceKey;
  return ALTERNATIVES.filter((a) => a.primary.slug === target);
};
